"use client"

import { usePreventScrollPenetration } from "@repo/ui/utils"
import dynamic from "next/dynamic"
import { useRef } from "react"

import { MainNavigationBar } from "./components"
import { useHeaderMarginTop, useHeaderScrollState } from "./hooks"
import { MenuItem } from "./types"
import { useCurrentUrl, useMobileMenu } from "./utils"

import "./index.scss"

import { ConfigProvider } from "antd"

import { IHeaderCoreProps } from "../../interface"
import { AnnouncementItem } from "../../servers/newscenter/interface"
import { NavigationItem } from "../MainNavigation/type"
import { useUserInfo } from "../UserProvider"

/** 将 Dropdown/Menu 的弹出层统一挂载到 header 内，便于样式作用域与 z-index 控制 */
const getHeaderPopupContainer = (triggerNode: HTMLElement | undefined): HTMLElement =>
  (triggerNode?.closest?.(".headerWrapper") ?? document.querySelector(".headerWrapper") ?? document.body) as HTMLElement

const MobileMenuDynamic = dynamic(() => import("./components/MobileMenu").then((mod) => mod.MobileMenu), { ssr: false })
const MobileSearchDynamic = dynamic(() => import("./components/MobileSearch").then((mod) => mod.MobileSearch), {
  ssr: false,
})

type HeaderContentWithScrollClientProps = Omit<IHeaderCoreProps, "markets"> & {
  menu: MenuItem[]
  menuItems: NavigationItem[]
  announcements?: AnnouncementItem[]
  initialHeaderMarginTopDesktop?: number
  initialHeaderMarginTopMobile?: number
}

/**
 * 客户端组件：最小化客户端代码，只处理滚动状态与交互逻辑
 * - 滚动状态监听
 * - 移动端菜单状态管理
 * - 动态 URL 获取
 * - Header 高度动态计算
 */
export const HeaderContentWithScrollClient: React.FC<React.PropsWithChildren<HeaderContentWithScrollClientProps>> = ({
  children,
  className = "",
  transparentMode = true,
  logo = {
    href: "/",
    src: `https://imgqn.smm.cn/static/image/metal/logo.svg`,
  },
  menu,
  menuItems,
  initialHeaderMarginTopDesktop = 0,
  initialHeaderMarginTopMobile = 0,
}) => {
  const mobileMenuState = useMobileMenu()
  const { userInfo } = useUserInfo()
  const { isMobileMenuOpen, isMobileSearchOpen, toggleMobileMenu, toggleMobileSearch, setIsMobileSearchOpen } =
    mobileMenuState

  const headerRef = useRef<HTMLElement | null>(null)
  const currentUrl = useCurrentUrl()

  // 获取滚动容器的 ref，用于防止滚动穿透
  const scrollContainerRef = usePreventScrollPenetration(isMobileMenuOpen)

  // 滚动状态监听（客户端必需）
  const scrollState = useHeaderScrollState(transparentMode)

  // 根据 Header 实际高度动态为 main 设置 margin-top，避免内容被固定头部遮挡（客户端必需）
  useHeaderMarginTop(headerRef, transparentMode, initialHeaderMarginTopDesktop, initialHeaderMarginTopMobile)

  return (
    <ConfigProvider getPopupContainer={getHeaderPopupContainer}>
      <header ref={headerRef} className={`headerWrapper ${scrollState} ${className}`}>
        {children}
        {/* 主导航栏 */}
        <MainNavigationBar
          scrollState={scrollState}
          logo={logo}
          menuItems={menuItems}
          onMobileMenuToggle={toggleMobileMenu}
          onMobileSearchToggle={toggleMobileSearch}
          isMobileMenuOpen={isMobileMenuOpen}
          isMobileSearchOpen={isMobileSearchOpen}
        />

        {/* 移动端搜索框 */}
        <MobileSearchDynamic isOpen={isMobileSearchOpen} onClose={() => setIsMobileSearchOpen(false)} />

        {/* 移动端菜单 */}
        <MobileMenuDynamic
          isOpen={isMobileMenuOpen}
          menu={menu}
          userInfo={userInfo}
          mobileMenuState={mobileMenuState}
          scrollContainerRef={scrollContainerRef}
          currentUrl={currentUrl}
        />
      </header>
    </ConfigProvider>
  )
}
